import './styles.scss';

import { EditOutlined, MoreOutlined } from '@ant-design/icons';
import { useNavigation } from '@refinedev/core';
import { Button, Dropdown } from 'antd';
import { KeyboardEvent, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSIONS } from '../../constants/permission';
import { IMarketplace } from '../../interfaces/marketplace';
import { convertPrice, getPublicMediaUrl } from '../../utils/resource';

type ApplicationItemProps = {
    application: IMarketplace.IApplication;
    permission: string[];
    ignoreFooter?: boolean;
    customClasses?: string;
};

/** Inline `style="…"` from the rich-text editor would override the card typography. */
const stripInlineStyles = (html: string) => (html ?? '').replace(/style="(.*?)"/gm, '');
const plainText = (html: string) => html.replace(/<[^>]*>/g, '').trim();

/**
 * Marketplace app card. The whole card opens the app (click, Enter or Space) when the user may view it; the footer shows
 * the first plan's price with its billing period, and editors get a "more" menu.
 */
export const ApplicationItem: React.FC<ApplicationItemProps> = ({
    application,
    permission,
    ignoreFooter,
    customClasses,
}) => {
    const { t } = useTranslation(['marketplace']);
    const { show, edit } = useNavigation();
    const [avatarFailed, setAvatarFailed] = useState(false);
    const canShow = permission.includes(PERMISSIONS.MARKETPLACE_SHOW);
    const canEdit = permission.includes(PERMISSIONS.MARKETPLACE_EDIT);

    const description = useMemo(
        () => stripInlineStyles(application.description),
        [application.description],
    );
    // Many apps repeat the title as the description: showing it twice is noise.
    const showDescription =
        !!plainText(description) && plainText(description) !== application.title?.trim();
    const plan = application.plans?.[0];
    const avatarUrl = application.avatar ? getPublicMediaUrl(application.avatar) : '';

    const open = () => canShow && show('marketplace', application.id);
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
        }
    };
    const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();

    const priceLabel = () => {
        if (!plan) return '—';
        if (!plan.pricing) return t('marketplace.free');
        const period = t(`marketplace.per.${plan.frequency}`, { defaultValue: '' });
        return `${convertPrice(plan.pricing)}${period ? ` ${period}` : ''}`;
    };

    return (
        <article
            className={`application-item-page-list${canShow ? ' is-clickable' : ''} ${
                customClasses ?? ''
            }`}
            role={canShow ? 'link' : undefined}
            tabIndex={canShow ? 0 : undefined}
            aria-label={application.title}
            onClick={open}
            onKeyDown={canShow ? onKeyDown : undefined}
        >
            <header className="app-card-header">
                <div className="app-card-avatar" aria-hidden="true">
                    {avatarUrl && !avatarFailed ? (
                        <img
                            src={avatarUrl}
                            alt=""
                            loading="lazy"
                            onError={() => setAvatarFailed(true)}
                        />
                    ) : (
                        <span>{(application.title ?? '?').trim().charAt(0).toUpperCase()}</span>
                    )}
                </div>
                <div className="app-card-tags">
                    {application.category?.name && (
                        <span className="app-card-category">{application.category.name}</span>
                    )}
                </div>
            </header>

            <h3 className="app-card-title">{application.title}</h3>
            {showDescription && (
                <div
                    className="app-card-description"
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            )}

            {!ignoreFooter && (
                <footer className="app-card-footer">
                    <span className={`app-card-price${plan && !plan.pricing ? ' is-free' : ''}`}>
                        {priceLabel()}
                    </span>
                    {canEdit && (
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            menu={{
                                items: [
                                    {
                                        key: 'edit',
                                        icon: <EditOutlined />,
                                        label: t('marketplace.edit'),
                                        onClick: ({ domEvent }) => {
                                            domEvent.stopPropagation();
                                            edit('marketplace', application.id);
                                        },
                                    },
                                ],
                            }}
                        >
                            <Button
                                type="text"
                                icon={<MoreOutlined />}
                                aria-label={t('marketplace.moreActions', {
                                    title: application.title,
                                })}
                                onClick={stop}
                                onKeyDown={stop}
                                className="app-card-more"
                            />
                        </Dropdown>
                    )}
                </footer>
            )}
        </article>
    );
};
